// src/components/orders/OrderDetailsUtils/helpers.js
import {format, parseISO} from 'date-fns';

/**
 * Format a date string to MM/dd/yyyy
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
        if (dateString.includes('T')) {
            return format(parseISO(dateString), 'MM/dd/yyyy');
        }
        return format(new Date(dateString + 'T00:00:00'), 'MM/dd/yyyy');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Calculate additional price based on invoice data
 * @param {Object} invoiceData - Invoice data
 * @returns {number} - Additional price
 */
export const calculateAdditionalPrice = (invoiceData) => {
    if (!invoiceData) return 0;

    const {
        totalAssemblyCost = 0,
        totalDrawerCost = 0,
        totalFinishingCost = 0,
        totalHardwareCost = 0,
        remainingBalance = 0
    } = invoiceData;

    return (totalAssemblyCost + totalFinishingCost + totalDrawerCost + totalHardwareCost) + remainingBalance;
};

/**
 * Format address string
 * @param {Object} addressInfo - Address information
 * @returns {string} - Formatted address
 */
export const formatAddress = (addressInfo) => {
    const {
        address1 = '',
        address2 = '',
        apartment = '',
        city = '',
        state = '',
        zipcode = ''
    } = addressInfo;

    const addr2 = address2 === 'null' || address2 === null ? '' : address2;
    const apt = apartment === 'null' || apartment === null ? '' : apartment;
    const cityStr = city === 'null' || city === null || city === '' ? '' : `${city},`;
    const stateStr = state === 'null' || state === null ? '' : state;
    const zip = zipcode === 'null' || zipcode === null ? '' : zipcode;

    return {
        line1: `${address1} ${addr2} ${apt}`.trim(),
        line2: `${cityStr}${stateStr} ${zip}`.trim()
    };
};

/**
 * Format project address
 * @param {Object} projectInfo - Project address information
 * @returns {Object} - Formatted project address
 */
export const formatProjectAddress = (projectInfo) => {
    const {
        projectAddress1 = '',
        projectAddress2 = '',
        projectCity = '',
        projectState = '',
        projectZipcode = ''
    } = projectInfo;

    const line1 = `${projectAddress1}${projectAddress2 ? ', ' + projectAddress2 : ''}`;
    const line2 = `${projectCity ? projectCity : ''}${projectState ? ', ' + projectState : ''}${projectZipcode ? ' ' + projectZipcode : ''}`;

    return {line1, line2};
};

/**
 * Check if user is authorized to view the order
 * @param {Object} user - User information
 * @param {Object} order - Order information
 * @returns {boolean} - Whether the user is authorized
 */
export const isUserAuthorized = (user, order) => {
    if (!user || !order) return false;

    const currentEmail = user.email?.trim().toLowerCase();
    const orderEmail = order.email?.trim().toLowerCase();
    const isAdmin = ['mark@thesealab.com', 'zach@thesealab.com'].includes(currentEmail);
    const isOwner = currentEmail === orderEmail;

    return isOwner || isAdmin;

};

/**
 * Check if totals have changed based on original and current options
 * @param {Object} order - Order information
 * @param {Object} originalOptions - Original order options
 * @param {Object} currentOptions - Current order options
 * @returns {boolean} - Whether totals have changed
 */
export const haveTotalsChanged = (order, originalOptions, currentOptions) => {
    if (!order || !originalOptions || !currentOptions) return false;

    let changed = false;

    if (!order.hardwarePaid && originalOptions.includeHardware !== currentOptions.includeHardware) {
        changed = true;
    }
    if (!order.assemblyPaid && originalOptions.includeAssembly !== currentOptions.includeAssembly) {
        changed = true;
    }
    if (!order.finishingPaid && originalOptions.includeFinishing !== currentOptions.includeFinishing) {
        changed = true;
    }
    if (!order.drawerBoxesPaid && originalOptions.includeDrawerboxes !== currentOptions.includeDrawerboxes) {
        changed = true;
    }

    return changed;
};

// Constants
export const SALES_TAX_RATE = 1.08875;